import { Component, inject, input, output, signal, computed, effect, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RequestService, Request } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { StepApprovalModalComponent, StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';
import { FormMode } from 'app/core/constants/form-mode';
import { AuthService } from '@services/core/auth.service';
import { CatalogService } from '@services/data/catalog.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-request-transfer',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    StepApprovalModalComponent
  ],
  templateUrl: './request-transfer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-transfer.component.scss']
})
export class RequestTransferComponent {
  request = input.required<Request>();
  reviewCompleted = output<void>();

  @ViewChild(StepApprovalModalComponent) approvalModal!: StepApprovalModalComponent;

  private requestService = inject(RequestService);
  private bpmService = inject(BpmService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private catalogService = inject(CatalogService);

  public partyForm!: FormGroup;
  public formMode = FormMode;

  selectedRequest = signal<Request | null>(null);
  isProcessingApproval = signal<boolean>(false);

  private role = computed(() => {
    const roleObj = this.authService.sharingRole() as any;
    return (roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  });

  canApprove = computed(() => this.bpmService.canApprove(this.selectedRequest(), this.role()));
  nextStatusSlug = computed(() => this.bpmService.nextStatusSlug(this.selectedRequest()));
  documentGloss = computed(() => this.bpmService.documentGloss(this.selectedRequest()));

  constructor() {
    this.partyForm = this.fb.group({
      uuid: [null],
      rut: [null],
      partyTypeCode: [null],
      names: [null],
      birthday: [null],
      mail: [null],
      mobileNumber: [null],
      groupCode: [null],
      entityCode: [null],
    });

    effect(() => {
      const req = this.request();
      this.selectedRequest.set(req);
      if (req?.party) {
        this.partyForm.patchValue(req.party);
      }
    }, { allowSignalWrites: true });
  }

  onReviewCompleted() {
    this.reviewCompleted.emit();
  }

  getEntityName(code: number | string): string {
    if (!code) return 'N/A';
    const entity = this.catalogService.entities().find(e => e.code == code);
    return entity ? `${entity.code} - ${entity.name}` : `Entidad ${code}`;
  }

  getProcessName(slug: string): string {
    if (!slug) return 'Proceso';
    const process = this.catalogService.processes().find(p => p.slug === slug || p.code === slug);
    return process?.name ?? `Proceso ${slug}`;
  }

  async approveRequest(): Promise<void> {
    const req = this.selectedRequest();
    if (!req || this.isProcessingApproval()) return;

    if (this.bpmService.stepRequiresDocument(req)) {
      this.approvalModal?.open();
      return;
    }

    const result = await Swal.fire({
      title: '¿Confirmar Recepción?',
      text: 'Se aceptará la transferencia del socio a su entidad.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Aceptar'
    });
    if (result.isConfirmed) {
      await this.executeDirectApproval(req);
    }
  }

  async onStepApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    const req = this.selectedRequest();
    if (!req || this.isProcessingApproval()) return;
    this.isProcessingApproval.set(true);
    try {
      await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
        toStatusSlug: this.nextStatusSlug(),
        gloss: payload.gloss,
        documentUuid: payload.documentUuid,
        documentTypeName: payload.documentTypeName,
        requiresDocument: payload.requiresDocument
      }));
      this.approvalModal?.forceClose();
      this.onReviewCompleted();
    } catch (err: any) {
      await Swal.fire({ icon: 'error', title: 'Error al procesar', text: err?.message ?? 'No se pudo registrar la aprobación.' });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async executeDirectApproval(req: Request): Promise<void> {
    this.isProcessingApproval.set(true);
    try {
      await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
        toStatusSlug: this.nextStatusSlug()
      }));
      this.onReviewCompleted();
    } catch (err: any) {
      await Swal.fire({ icon: 'error', title: 'Error al procesar', text: err?.message ?? 'No se pudo registrar la aprobación.' });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  async rejectRequest(): Promise<void> {
    const req = this.selectedRequest();
    if (!req || this.isProcessingApproval()) return;
    const { value: reason } = await Swal.fire({
      title: 'Rechazar Solicitud',
      input: 'textarea',
      inputLabel: 'Motivo del Rechazo (Obligatorio)',
      inputPlaceholder: 'Escriba por qué se rechaza...',
      inputValidator: (v) => !v ? 'El motivo es obligatorio para rechazar' : null,
      showCancelButton: true,
      confirmButtonText: 'Confirmar Rechazo',
      confirmButtonColor: '#d33'
    });
    if (!reason) return;
    this.requestService.advanceFlowStep(req.uuid, {
      toStatusSlug: 'REJECTED',
      reason
    }).subscribe(() => this.onReviewCompleted());
  }
}